import React from "react";
import { StatusIndicator } from "../../../components/statusIndicator";

const ActivityReport = ({ reportData }) => {
  function IncidentSeverity(value) {
    switch (value.toLowerCase()) {
      case "low":
        return <StatusIndicator text="Low" type="success" />;
      case "medium":
        return <StatusIndicator text="Medium" type="warning" />;
      case "high":
        return <StatusIndicator text="High" type="danger" />;
      default:
        return;
    }
  }

  return (
    <>
      <div className="pt-8 pb-6">
        <span className="text-sm block text-gray-800 dark:text-gray-100">
          Daily activity report for
        </span>
        <span className="text-xlg text-gray-800 dark:text-gray-100">
          {reportData.start_date !== reportData.end_date
            ? `${new Date(reportData.start_date).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })} - ${new Date(reportData.end_date).toLocaleDateString(
                undefined,
                {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }
              )}`
            : new Date(reportData.start_date).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
        </span>
      </div>
      <div>
        <span className="text-gray-700 dark:text-gray-300">Summary</span>
      </div>
      <div className="mt-2 grid grid-cols-5 gap-4">
        {reportData.summary &&
          Object.entries(reportData.summary).map(([key, value]) => {
            const count = Number(value) || 0;
            // Label is stored plural (e.g. "emergency_calls"); drop the trailing
            // "s" on the last word when the count is exactly 1, then sentence-case
            // (first letter only — no `capitalize` class, which would Title Case).
            const words = key.replace(/_/g, " ").split(" ");
            if (count === 1) {
              const last = words[words.length - 1];
              words[words.length - 1] = last.endsWith("s")
                ? last.slice(0, -1)
                : last;
            }
            const phrase = words.join(" ");
            const label = phrase.charAt(0).toUpperCase() + phrase.slice(1);
            return (
              <div key={key} className="bg-gray-50 dark:bg-gray-800 rounded-md p-4">
                <div className="text-xl font-bold text-gray-800 dark:text-gray-100">{count}</div>
                <div className="text-xsm text-gray-600 dark:text-gray-400">{label}</div>
              </div>
            );
          })}
      </div>
      <div className="mt-8">
        {reportData.sites &&
          reportData.sites.map((site) => {
            const sortedActivities = [...site.activities].sort(
              (a, b) =>
                new Date(a.activity_date).getTime() -
                new Date(b.activity_date).getTime()
            );
            return (
              <div key={site.site_id} className="mb-8">
                {/* Site Header */}
                <div className="mb-2">
                  <h3 className="text-gray-700 dark:text-gray-200">{site.site_name}</h3>
                  <p className="text-xsm text-gray-700 dark:text-gray-400">{site.address}</p>
                </div>
                <div>
                  <table className="min-w-full max-w-full table-fixed">
                    <thead className="text-left text-xxsm text-black dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="font-medium px-3 py-3 rounded-tl-lg rounded-bl-lg w-20">
                          Time
                        </th>
                        <th className="font-medium px-3 py-3 w-32">Guard</th>
                        <th className="font-medium px-3 py-3 w-48">
                          Activity type
                        </th>
                        <th className="font-semibold px-3 py-3 rounded-tr-lg rounded-br-lg">
                          Activity details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-left">
                      {sortedActivities.map((activity) => (
                        <React.Fragment key={activity.activity_log_id}>
                          <tr>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 w-20">
                              {new Date(
                                activity.activity_date
                              ).toLocaleTimeString(undefined, {
                                hour: "2-digit",
                                minute: "2-digit", hourCycle: "h23",
                              })}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 w-32">
                              {activity.guard_name}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 w-48">
                              {Array.isArray(activity.activity_type)
                                ? activity.activity_type.map((item, index) => (
                                    <span
                                      key={index}
                                      className={`block mr-1 ${
                                        index === 1 ? "text-xsm" : "text-sm"
                                      }`}
                                    >
                                      {item}
                                    </span>
                                  ))
                                : activity.activity_type}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {Array.isArray(activity.activity_type) &&
                              activity.activity_type[0].toLowerCase() ===
                                "incident report" ? (
                                <div className="inline-block w-auto ml-2">
                                  {IncidentSeverity(activity.activity_detail)}
                                </div>
                              ) : (
                                activity.activity_detail
                              )}
                            </td>
                            {/* <td className="pl-6 py-1 whitespace-nowrap text-sm text-gray-600 text-right">
                              {activity.media_count}{" "}
                              {activity.media_count === 1 ? "image" : "images"}
                            </td> */}
                          </tr>
                          {activity.media_urls &&
                            activity.media_urls.trim() !== "" && (
                              <tr>
                                <td
                                  colSpan="4"
                                  className="pt-0 px-3 pb-4 border-gray-100 dark:border-gray-800 border-b-2"
                                >
                                  <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-4 w-full">
                                    {activity.media_urls
                                      .split(",")
                                      .map((url, index) => (
                                        <img
                                          key={index}
                                          src={url}
                                          alt="Activity Media"
                                          className="w-full rounded shadow-sm"
                                        />
                                      ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};

export { ActivityReport };
